'use client'

import dynamic from 'next/dynamic'
import { LandingHero, WhoWeAreSection } from '@/components/Landing'

const LocationsMapSection = dynamic(
  () =>
    import('@/components/Landing/LocationsMapSection').then(
      (mod) => mod.LocationsMapSection,
    ),
)
const WhyNeonWood = dynamic(
  () => import('@/components/Landing/WhyNeonWood').then((mod) => mod.WhyNeonWood),
)
const ApartmentAvailabilitySection = dynamic(
  () =>
    import('@/components/Landing/ApartmentAvailabilitySection').then(
      (mod) => mod.ApartmentAvailabilitySection,
    ),
)
const TestimonialsSection = dynamic(
  () => import('@/components/Landing/TestimonialsSection'),
)
const VideoGallerySection = dynamic(
  () =>
    import('@/components/Landing/VideoGallerySection').then(
      (mod) => mod.VideoGallerySection,
    ),
)
const BookingSteps = dynamic(
  () => import('@/components/Landing/BookingSteps').then((mod) => mod.BookingSteps),
)
const FAQSection = dynamic(
  () => import('@/components/Landing/FAQSection').then((mod) => mod.FAQSection),
)
const BlogSection = dynamic(
  () => import('@/components/Landing/BlogSection').then((mod) => mod.BlogSection),
)
import type { PolaroidGalleryItem } from '@/components/PolaroidGalleryCarousel'
import type { BlogPostData } from '@/components/Landing/BlogSection'
import type { MapCityCardData } from '@/lib/cities/mapCityData'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import type { AvailabilitySectionItem } from '@/lib/apartments/availabilitySectionData'
import type { LocationCommunitySpaces } from '@/lib/locations/locationCommunitySpaces'
import type { FaqSectionData } from '@/lib/faqs/faqData'
import type { ResolvedHeroBackground } from '@/lib/hero/resolveHeroBackgroundMedia'
import type { ImageGalleryStripSlide } from '@/components/Gallery/imageGalleryStrip'
import type { ResolvedPopupVideo } from '@/lib/media/resolvePopupVideo'
import type { TestimonialCardData } from '@/lib/testimonials/testimonialCard'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import React, { useEffect } from 'react'

type PageClientProps = {
  apartments: AvailabilitySectionItem[]
  communitySpacesByLocation: Record<string, LocationCommunitySpaces>
  faqData: FaqSectionData
  heroBackground?: ResolvedHeroBackground
  mapCities: MapCityCardData[]
  mapLocations: MapLocationCardData[]
  posts: BlogPostData[]
  polaroidGalleryItems: PolaroidGalleryItem[]
  testimonials: TestimonialCardData[]
  videoGalleryPopup?: ResolvedPopupVideo | null
  journeyGallerySlides?: ImageGalleryStripSlide[]
}

const PageClient: React.FC<PageClientProps> = ({
  apartments,
  communitySpacesByLocation,
  faqData,
  heroBackground,
  mapCities,
  mapLocations,
  posts,
  polaroidGalleryItems,
  testimonials,
  videoGalleryPopup,
  journeyGallerySlides,
}) => {
  const { setHeaderTheme } = useHeaderTheme()

  useEffect(() => {
    setHeaderTheme('dark')
  }, [setHeaderTheme])

  return (
    <>
      <LandingHero background={heroBackground} />
      <WhoWeAreSection />
      <LocationsMapSection mapCities={mapCities} mapLocations={mapLocations} />
      <WhyNeonWood polaroidGalleryItems={polaroidGalleryItems} />
      <ApartmentAvailabilitySection
        apartments={apartments}
        communitySpacesByLocation={communitySpacesByLocation}
        variant="landing"
      />
      <TestimonialsSection testimonials={testimonials} />
      <VideoGallerySection
        slides={journeyGallerySlides}
        video={videoGalleryPopup ?? undefined}
      />
      <BookingSteps />
      <FAQSection faqData={faqData} />
      <BlogSection posts={posts} />
    </>
  )
}

export default PageClient
